import { intl } from '../../../i18n'
import { SelectOption } from '../../model/selectOption'

type SearchableMilestone = {
  name: string
  wbsItemUuid?: string
}

/** ひらがなをカタカナへ寄せるためのコードポイント差 */
const HIRAGANA_TO_KATAKANA_OFFSET = 0x60

/**
 * 表記ゆれを吸収してから比較するための正規化。
 * NFKCで全角英数・半角カナを揃え、大文字小文字とひらがな/カタカナの差も無くす。
 */
const normalizeForSearch = (value: string): string =>
  value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[ぁ-ゖ]/g, char =>
      String.fromCharCode(char.charCodeAt(0) + HIRAGANA_TO_KATAKANA_OFFSET)
    )

const tokenize = (searchQuery: string): string[] =>
  normalizeForSearch(searchQuery)
    .split(/\s+/)
    .filter(token => token.length > 0)

/**
 * マイルストーン一覧の絞り込み述語を作る。検索文字列が空の場合は undefined(絞り込みなし)。
 *
 * マイルストーン名と対象名(パスを含む)をまとめて検索対象にし、空白区切りの語を
 * すべて含むものだけを残す。語ごとに項目をまたいで一致してよいので、
 * 「設計 リリース」のように対象名とマイルストーン名を組み合わせて絞り込める。
 * 対象未設定のものは一覧に「全体」と表示されるため、その表示名でも引っかかる。
 */
export const createMilestoneMatcher = (
  targetOptions: SelectOption[],
  searchQuery: string
): ((milestone: SearchableMilestone) => boolean) | undefined => {
  const tokens = tokenize(searchQuery)
  if (tokens.length === 0) return undefined

  const targetTextByUuid = new Map(
    targetOptions.map(option => [
      option.value,
      normalizeForSearch(`${option.name} ${option.tooltip ?? ''}`),
    ])
  )
  const wholeProjectText = normalizeForSearch(
    intl.formatMessage({ id: 'whole.project' })
  )

  return milestone => {
    const targetText = milestone.wbsItemUuid
      ? (targetTextByUuid.get(milestone.wbsItemUuid) ?? '')
      : wholeProjectText
    const searchableText = `${normalizeForSearch(milestone.name)} ${targetText}`
    return tokens.every(token => searchableText.includes(token))
  }
}
